import { useEffect, useState } from 'react'
import { CollapsibleSection } from './CollapsibleSection'
import { SensorPort, SensorType } from '@atcreate-inc/sigfox-decoder/dist/common'
import { convertToSensorId } from '../common/utils'
import { api } from '../services'
import { useAccountContext } from '../contexts'

interface Props {
  sigfoxId: string
  sensorConfigs: Record<string, any>
}

export function DeviceSensorSettings({ sigfoxId, sensorConfigs }: Props) {
  const { devices } = useAccountContext()
  const device = devices[sigfoxId]

  const showC3 = false // TODO: fix this
  const sensors: string[] = Object.values(sensorConfigs).map((config) => config.sensorProductName)

  const [isLoading, setIsLoading] = useState(false)
  const [title, setTitle] = useState('Sensor Readings: ')
  const [sensorDepth, setSensorDepth] = useState<Record<string, number>>({})
  const [sensorLowThreshold, setSensorLowThreshold] = useState<Record<string, number>>({})
  const [sensorHighThreshold, setSensorHighThreshold] = useState<Record<string, number>>({})
  const [sensorLowAlertEnabled, setSensorLowAlertEnabled] = useState<Record<string, boolean>>({})
  const [sensorHighAlertEnabled, setSensorHighAlertEnabled] = useState<Record<string, boolean>>({})

  // set default values
  useEffect(() => {
    Object.values(SensorPort).forEach((port) => {
      if (!isLowAlertEnabled(port) && sensorLowThreshold[port]) {
        // do something
      }
      if (!isHighAlertEnabled(port) && sensorHighThreshold[port]) {
        // do something
      }
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (device) {
      setTitle(`Sensor Readings: Every ${device.scheduledMessagesFrequency} minutes`)

      if (sensors.includes('Vegetronix')) {
        sync()
      }
    }

    async function sync() {
      setIsLoading(true)

      try {
        const { deviceId } = device
        Object.values(SensorPort).forEach(async (port) => {
          const sensorId = convertToSensorId(port)

          const [response1, response2, response3] = await Promise.all([
            api.getSensorPosition(deviceId, sensorId),
            api.getSensorThresholds(deviceId, sensorId),
            api.getSensorAlerts(deviceId, sensorId),
          ])

          setSensorDepth((prev) => ({ ...prev, [port]: response1.sensorPosition }))
          setSensorLowThreshold((prev) => ({ ...prev, [port]: response2.lowThreshold }))
          setSensorHighThreshold((prev) => ({ ...prev, [port]: response2.highThreshold }))
          setSensorLowAlertEnabled((prev) => ({ ...prev, [port]: response3.lowAlertEnabled }))
          setSensorHighAlertEnabled((prev) => ({ ...prev, [port]: response3.highAlertEnabled }))
        })
      } catch (error) {
        console.error(error)
      } finally {
        setIsLoading(false)
      }
    }
  }, [device, sensors])

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function getSensorDepth(port: string) {
    return sensorDepth[port]
  }

  function isLowAlertEnabled(port: string) {
    return sensorLowAlertEnabled[port]
  }

  function isHighAlertEnabled(port: string) {
    return sensorHighAlertEnabled[port]
  }

  function getLowThreshold(port: string) {
    return sensorLowThreshold[port]
  }

  function getHighThreshold(port: string) {
    return sensorHighThreshold[port]
  }

  function updateSensorAlerts(port: string, enableLowAlert: boolean, enableHighAlert: boolean) {
    const sensorId = convertToSensorId(port)

    setIsLoading(true)
    try {
      api.setSensorAlerts(device.deviceId, sensorId, {
        enableLowAlert,
        enableHighAlert,
      })
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  function updateSensorThresholds(port: string, lowThreshold: number, highThreshold: number) {
    const sensorId = convertToSensorId(port)

    setIsLoading(true)
    try {
      api.setSensorThresholds(device.deviceId, sensorId, {
        lowThreshold,
        highThreshold,
      })
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <CollapsibleSection title={title} open={false} isLoading={isLoading}>
      <table className="w-full">
        <thead>
          <tr>
            <td className="w-1/12">
              <b>Port</b>
            </td>
            <td className="w-2/12">
              <b>Sensor Type</b>
            </td>
            <>
              {Object.values(sensorConfigs)
                .map((sensor) => sensor.sensorManufacturerName.toLowerCase())
                .includes('vegetronix') ? (
                <>
                  <td className="w-1/12">
                    <b>Depth</b>
                  </td>
                  <td className="w-1/12">
                    <b>Water-In Alert Enabled</b>
                  </td>
                  <td className="w-2/12">
                    <b>Water-In Threshold</b>
                  </td>
                  <td className="w-1/12">
                    <b>Water-Out Alert Enabled</b>
                  </td>
                  <td className="w-2/12">
                    <b>Water-Out Threshold</b>
                  </td>
                  <td className="w-1/12">
                    <b>{''}</b>
                  </td>
                </>
              ) : Object.values(sensorConfigs)
                  .map((sensor) => sensor.sensorManufacturerName.toLowerCase())
                  .includes('irrometer') ? (
                <>
                  <td className="w-1/12">
                    <b>Depth</b>
                  </td>
                  <td className="w-1/12">
                    <b>Alert</b>
                  </td>
                  <td className="w-2/12"></td>
                  <td className="w-1/12"></td>
                  <td className="w-2/12"></td>
                  <td className="w-1/12"></td>
                </>
              ) : (
                <>
                  <td className="w-1/12"></td>
                  <td className="w-1/12"></td>
                  <td className="w-2/12"></td>
                  <td className="w-1/12"></td>
                  <td className="w-2/12"></td>
                  <td className="w-1/12"></td>
                </>
              )}
            </>
          </tr>
        </thead>
        <tbody>
          {Object.keys(sensorConfigs).map((port, index) => {
            // const sensor = showC3 && port === 'C3' ? { sensorType: SensorType.AquaTroll500TemperatureSensor } : null
            const sensor =
              showC3 && port === 'C3' ? { sensorType: SensorType.AquaTroll500TemperatureSensor } : sensorConfigs[port]
            // : device.config.sensors[port] // TODO: fix this

            if (!sensor) return null

            const productName = sensor.sensorProductName

            return (
              <tr key={index} className={index % 2 === 0 ? tailwind.even : tailwind.odd}>
                {productName.toLowerCase().includes('vegetronix') ? (
                  <>
                    <td>{port}</td>
                    <td>{sensor.sensorProductName}</td>
                    <td className="">{`${sensor.conversionOffset}"`}</td>

                    <td className="">
                      <input
                        className="mx-3"
                        type="checkbox"
                        checked={isHighAlertEnabled(port)}
                        onChange={(e) => {
                          setSensorHighAlertEnabled((prev) => ({ ...prev, [port]: e.target.checked }))
                          updateSensorAlerts(port, isLowAlertEnabled(port), e.target.checked)
                        }}
                      />
                    </td>
                    <td className="">
                      <input
                        className={tailwind.inputText}
                        type="number"
                        value={getHighThreshold(port)}
                        placeholder="..."
                        onChange={(e) => {
                          let value = Number(e.target.value.replace(/^0+/, ''))
                          // value = value < 0 ? 0 : value
                          // value = value > 100 ? 100 : value
                          // value = value < getLowThreshold(port) ? getLowThreshold(port) : value
                          // value = Number(value.toString())
                          setSensorHighThreshold((prev) => ({ ...prev, [port]: value }))
                        }}
                        onBlur={(e) => {
                          const value = Number(e.target.value.replace(/^0+/, ''))
                          updateSensorThresholds(port, getLowThreshold(port), value)
                        }}
                      />
                      {' %'}
                    </td>

                    <td className="">
                      <input
                        className="mx-3"
                        type="checkbox"
                        checked={isLowAlertEnabled(port)}
                        onChange={(e) => {
                          setSensorLowAlertEnabled((prev) => ({ ...prev, [port]: e.target.checked }))
                          updateSensorAlerts(port, e.target.checked, isHighAlertEnabled(port))
                        }}
                      />
                    </td>
                    <td className="">
                      <input
                        className={tailwind.inputText}
                        type="number"
                        value={getLowThreshold(port)}
                        placeholder="..."
                        onChange={(e) => {
                          let value = Number(e.target.value.replace(/^0+/, ''))
                          // value = value < 0 ? 0 : value
                          // value = value > 100 ? 100 : value
                          // value = value > getHighThreshold(port) ? getHighThreshold(port) : value
                          setSensorLowThreshold((prev) => ({ ...prev, [port]: value }))
                        }}
                        onBlur={(e) => {
                          const value = Number(e.target.value.replace(/^0+/, ''))
                          updateSensorThresholds(port, value, getHighThreshold(port))
                        }}
                      />
                      {' %'}
                    </td>

                    <td>
                      {/* <div className="pl-2" onClick={() => {}}>
                        <FontAwesomeIcon icon={faRedo} />
                      </div> */}
                    </td>
                  </>
                ) : productName.toLowerCase().includes('irrometer') ? (
                  <>
                    <td>{port}</td>
                    <td>{sensor.sensorProductName}</td>
                    <td>{`${sensor.conversionOffset}"`}</td>
                    <td>{'not set'}</td>
                    <td> </td>
                    <td> </td>
                  </>
                ) : (
                  <>
                    <td>{port}</td>
                    <td>{sensor.sensorProductName}</td>
                    <td> </td>
                    <td> </td>
                    <td> </td>
                    <td> </td>
                  </>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
      {sensors.includes(SensorType.VegetronixVH400) && (
        <div className="flex justify-end m-3">
          <button className={tailwind.buttonEnabled} onClick={() => {}}>
            Save
          </button>
        </div>
      )}
    </CollapsibleSection>
  )
}

const tailwind = {
  even: 'h-10 text-black text-l font-regular py-2.5 pl-4 pr-4 w-full border-b bg-[#f9f9f9]',
  odd: 'h-10 text-black text-l font-regular py-2.5 pl-4 pr-4 w-full border-b bg-[#fff]',
  inputText:
    'appearance-none border border-red-500 rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none w-[100px] text-center',
  buttonEnabled:
    'bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-4 rounded focus:outline-none focus:shadow-outline mb-1',
  buttonDisabled: 'bg-gray-400 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline mb-3',
}
